//handling string, number, boolean, null, undefined, nested properties
//supported ascending and descending toggle

import { useState, useMemo } from "react";

/**
 * Custom hook for table sorting functionality
 * @param {Array} data - Array of items to sort
 * @param {Object} config - Configuration object
 * @param {string} config.defaultSortKey - Default column to sort by
 * @param {string} config.defaultSortDirection - Default sort direction ('asc' or 'desc')
 * @returns {Object} - Sorting state and functions
 */
export const useSorting = (data = [], config = {}) => {
  const { defaultSortKey = null, defaultSortDirection = "asc" } = config;

  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new sort key and default to ascending
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !data || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];

      // Handle nested properties (e.g., "user.name")
      if (sortKey.includes(".")) {
        const keys = sortKey.split(".");
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle boolean values
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        return sortDirection === "asc"
          ? aValue === bValue
            ? 0
            : aValue
              ? 1
              : -1
          : aValue === bValue
            ? 0
            : aValue
              ? -1
              : 1;
      }

      // Handle string values
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      }

      // Handle number values
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Fallback: convert to string and compare
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const comparison = aStr.localeCompare(bStr);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [data, sortKey, sortDirection]);

  const getSortIcon = (columnKey) => {
    if (sortKey !== columnKey) {
      return null; // No sort icon
    }
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const getSortClassName = (columnKey) => {
    if (sortKey !== columnKey) {
      return "";
    }
    return sortDirection === "asc" ? "sort-asc" : "sort-desc";
  };

  return {
    sortedData,
    sortKey,
    sortDirection,
    handleSort,
    getSortIcon,
    getSortClassName,
  };
};

export default useSorting;
